import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gql, useLazyQuery } from '@apollo/client';
import { differenceInDays } from 'date-fns';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TextField, Box, Button, Grid, Autocomplete, FormControl, Select, InputLabel, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../features/auth/authSlice';
import { IProject } from '../../../types/project';
import { docusignStatus } from '../../../utils/Constants';
import { IDocuments } from '../../../types/docusign';
import LoadingLogo from '../../common/LoadingLogo';
import { convertAllDates, downloadExcel, downloadPdf, capitalizeFirstLetter, stringWithSpaces } from '../../../utils/Functions';
import StandardTable from '../../tables/StandardTable';
import { subDays } from 'date-fns';

const Envelopes = () => {
  const user = useSelector(selectUser);
  const [dateStart, setDateStart] = useState<Date | null>(new Date(subDays(new Date().setHours(0, 0, 0, 0), 7)));
  const [dateEnd, setDateEnd] = useState<Date | null>(new Date());
  const [project, setProject] = useState<IProject | null>(null);
  const [status, setStatus] = useState<string>('All');
  const [count, setCount] = useState<number>(0);
  const [documents, setDocuments] = useState<IDocuments[]>([]);

  const [getEnvelopes, { loading }] = useLazyQuery(ENVELOPES, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setCount(data.getEnvelopes.count)
      setDocuments(data.getEnvelopes.documents)
    },
  });

  useEffect(() => {
    getEnvelopes({ variables: { status, dateStart, dateEnd } });
  }, [])

  const columns = useMemo(() => {
    return [
      {
        Header: 'Suite',
        accessor: (rowData: any) => {
          if (rowData.suite) {
            return (
              <Link
                style={{ textDecoration: 'none', color: 'inherit' }}
                rel="noopener noreferrer"
                target="_blank"
                to={`/${rowData.project}/dashboard/${rowData.unit}`}
              >
                <strong style={{ cursor: 'pointer' }}>{rowData.suite}</strong>
              </Link>
            );
          } else {
            return 'N/A'
          }
        },
      },
      {
        Header: 'Project',
        accessor: (rowData: any) => rowData.projectName,
      },
      {
        Header: 'Document Name',
        accessor: (rowData: any) => rowData.name,
      },
      {
        Header: 'Status',
        accessor: (rowData: any) => stringWithSpaces(rowData.status),
      },
      {
        Header: 'Display Status',
        accessor: (rowData: any) => capitalizeFirstLetter(rowData.displayStatus),
      },
      {
        Header: 'Signers',
        accessor: (rowData: any) => {
          if (rowData.signerDetails && rowData.signerDetails.length) {
            return (
              rowData.signerDetails.map((signer: any) => {
                return (
                  <Box sx={{ mb: 2 }}>
                    <Box>Signer: {signer.name}</Box>
                    <Box>Email: {signer.email}</Box>
                    <Box sx={{ color: signer.status === 'Completed' ? 'green' : 'red'}}>Status: {stringWithSpaces(signer.status)}</Box>
                  </Box>
                )
              })
            )
          } 
        },
      },
      {
        Header: 'Created Date',
        accessor: (rowData: any) => {
          return (
            <Box sx={{ color: '#000' }}>
              {rowData.status !== 'completed' ? (rowData.sentDate ? convertAllDates(rowData.sentDate, 'PPpp') : 'N/A') : 'N/A'}
            </Box>
          );
        },
      },
      {
        Header: 'Envelope Expiry Date',
        accessor: (rowData: any) => {
          let color = '#000';
          if (differenceInDays(new Date(rowData.expiryDate), new Date()) < 15) {
            color = 'orange';
          } else if (new Date(rowData.expiryDate).valueOf() < new Date().valueOf()) color = 'red';

          return (
            <Box sx={{ color }}>
              {rowData.status !== 'completed' ? (rowData.expiryDate ? convertAllDates(rowData.expiryDate, 'PPpp') : 'N/A') : 'N/A'}
            </Box>
          );
        },
      },
    ];
  }, []);

  const handleEnvelopes = () => {
    getEnvelopes({ variables: { project: project?._id, status, dateStart, dateEnd } });
  };

  const download = (type: string, data: any) => {
    let headers = [
      {
        label: 'Suite',
        id: 'suite',
      },
      {
        label: 'Envelope Name',
        id: 'name',
      },
      {
        label: 'Status',
        id: 'status',
      },
      {
        label: 'Envelope Created Date',
        id: 'createdAt',
      },
      {
        label: 'Envelope Expiry Date',
        id: 'expiredAt',
      },
    ];

    let widths = {
      suite: 15,
      name: 15,
      status: 15,
      createdAt: 15,
      expiredAt: 15,
    };

    let pdfWidths = {
      suite: 200,
      name: 200,
      status: 200,
      createdAt: 200,
      expiredAt: 200,
    };

    let newData = data.map((data: any) => {
      return {
        suite: data.suite,
        name: data.name,
        status: capitalizeFirstLetter(data.status),
        createdAt: convertAllDates(new Date(data.sentDate), 'PPpp'),
        expiredAt: convertAllDates(new Date(data.expiryDate), 'PPpp'),
      };
    });

    let sheetTitle = `${project ? project.name : 'Documents'}`;

    if (type === 'excel') {
      downloadExcel([newData], [headers], [], [[widths]], [sheetTitle], sheetTitle);
    } else {
      downloadPdf([newData], [headers], [], [pdfWidths], [sheetTitle], sheetTitle);
    }
  };

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}>
            <Autocomplete
              sx={{ height: '100%' }}
              options={[user?.projectAccess!.map((projectAccess: any) => projectAccess?.project)].flat()}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              disableClearable={false}
              freeSolo={false}
              value={project}
              onChange={(e, value) => {
                setProject(value!);
              }}
              renderInput={(params) => <TextField {...params} label="Project" size="medium" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel id="id-question-label">Document Status</InputLabel>
              <Select
                value={status}
                labelId="id-status-label"
                id="id-status"
                label="Document Status"
                name="status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="All">All</MenuItem>
                {docusignStatus.map((status: string) => {
                  return <MenuItem value={status}>{status}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimePicker
              label={'Document Start Date (YYYY/MM/DD)'}
              value={dateStart}
              onChange={(newValue) => {
                setDateStart(newValue);
              }}
              sx={{ width: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DateTimePicker
              label={'Document End Date (YYYY/MM/DD)'}
              value={dateEnd}
              onChange={(newValue) => {
                setDateEnd(newValue);
              }}
              sx={{ width: '100%' }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button sx={{ height: '100%' }} variant="contained" color="primary" onClick={() => handleEnvelopes()}>
              Search
            </Button>
          </Grid>
        </Grid>
      </LocalizationProvider>
      {loading ? (
        <Box
          sx={{
            textAlign: 'center',
          }}
        >
          <LoadingLogo />
        </Box>
      ) : documents.length ? (
        <Box>
          <Box>
            <StandardTable data={documents} columns={columns} download={download} />
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center' }}>
          <em>No Envelopes Found!</em>
        </Box>
      )}
    </Box>
  );
};

const ENVELOPES = gql`
  query getEnvelopes($project: MongoID, $status: String!, $dateStart: Date!, $dateEnd: Date!) {
    getEnvelopes(project: $project, status: $status, dateStart: $dateStart, dateEnd: $dateEnd) {
      total 
      documents {
        suite
        project
        projectName
        unit
        name
        status
        displayStatus
        expiryDate
        sentDate
        signerDetails {
          name
          email
          status
        }
      }
    }
  }
`;

export default Envelopes;
